//angular imports
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  resource,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { filter, map, Subject, Subscription, takeUntil } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";

//local imports
import {
  CatalogConstants,
  CatalogDataChangedMessage,
  CatalogStateService,
  CategoryModel,
  DocumentModel,
  HidePrintRoomPropertiesMessage,
  PrintRoomPropertiesMessage,
  PropertyType,
  ShowPrintRoomPropertiesMessage,
} from "@app/catalog";
import { CatalogCommonConstants } from "@app/catalog-common";
import { ApplicationSecurity, SearchModel, SecurityConfig } from "@app/core";
import {
  BreadcrumbChangedMessage,
  BreadcrumbItemToggleMessage,
  IBreadcrumbItemModel,
  Messenger,
  UnityConfig,
  UnityParams
} from "@app/shared";
import { CommonConstants } from "@app/app-common";
import { MsalBroadcastService } from "@azure/msal-angular";
import { AuthenticationResult, EventMessage, EventType } from "@azure/msal-browser";

@Component({
  selector: "room-property",
  standalone: false,
  templateUrl: "./room-property.component.html",
  styleUrl: "./room-property.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomPropertyComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private _authSubscription: Subscription;
  private catalogStateService = inject(CatalogStateService);
  private readonly _destroying$ = new Subject<void>();
  private selectedCategoryId = signal<string>(undefined);  
  private currentBreadcrumbs: IBreadcrumbItemModel[] = [];
  


  public propertyType = PropertyType;
  public catalogMenuChanged: Signal<CatalogDataChangedMessage>;

  public readonly is3DViewerEnable = signal(false);
  
  private _accessToken : WritableSignal<string> = signal(null);
  public catalogConstants = CatalogConstants;

  public menuLoaded = computed(() => {
    let msg = this.catalogMenuChanged();
    return !!msg;
  });

  public roomId = toSignal(
     this.route.paramMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.ID)))
  );

  private departmentId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.DEPARTMENT_ID)))
  );

  private programId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.PROGRAM_ID)))
  );

  private projectId = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(CatalogCommonConstants.ROUTE_PARAMS.PROJECT_ID)))
  );


  public filteredCategories = computed(() => {
      const selectedId = this.selectedCategoryId();
      const categories = this.roomPropertyResource.value()?.categories() ?? [];

      return selectedId
        ? categories.filter(cat => cat.id === selectedId)
        : categories;
    });


  public roomPropertyResource = resource({
    request: () => ({
      id: this.roomId(),      
      departmentId: this.departmentId(),
      programId: this.programId(),
      projectId: this.projectId(),
    }),
    loader: async ({ request }) => {
      const property = await this.catalogStateService.getRoomProperty(
        request.id,
        request.departmentId,
        request.programId,
        request.projectId,
      );

      if (property.documents) {
        const filteredDocuments = property.documents().filter(
          (doc: DocumentModel) => !doc.superseded()
        );
        property.documents.set(filteredDocuments);
      }
          
      return property;
    }
  });

  public RoomPropertyComponent: typeof RoomPropertyComponent = RoomPropertyComponent;


  public roomContents = computed(() => {
      const categories = this.roomPropertyResource.value().categories();
      const filteredCategories = this.selectedCategoryId() 
          ? categories.filter(cat => cat.id === this.selectedCategoryId())
          : categories;
      
      return filteredCategories
          .flatMap(cat => cat.roomContents())
          .sort((a, b) => {
            const sortKeyA = a.code() || a.name();
            const sortKeyB = b.code() || b.name();
            return sortKeyA.localeCompare(sortKeyB);
          });
  });
  
  public static cacheContext: any = {};

  public config = computed(() => {
    let c = new UnityConfig();

    c.dataUrl = "/assets/partviewer/Build/part-viewer.data.gz";
    c.frameworkUrl = "/assets/partviewer/Build/part-viewer.framework.js.gz";
    c.codeUrl = "/assets/partviewer/Build/part-viewer.wasm.gz";
    c.streamingAssetsUrl = "/assets/partviewer/StreamingAssets";
    c.productName = "Modutecture Part Viewer";
    return c;
  });

  public params: Signal<UnityParams[]> = computed(() => {
    
    let protectedResources = this.applicationSecurity.getProtectedResources();
            
    let scopes = protectedResources.applicationApi.scopes.read?.join(" ") ?? "" ;

    let accessToken = this._accessToken();    
    let refreshToken = this.GetRefreshToken();
      
    if (!refreshToken) {
      console.log('Refresh unavailable');
    }

    let partId = this.roomPropertyResource.value().partId() ?? "";
    let componentId = this.roomPropertyResource.value().componentId() ?? "";

    console.log("s", scopes, "p", partId, "c", componentId);
    
    var params = [ 
      { key: "SetAppConfig", value: JSON.stringify(this.securityConfig) },
      { key: "SetAccessToken", value: accessToken },
      { key: "SetRefreshToken", value: refreshToken },
      { key: "SetScopes", value: scopes },      
      { key: "SetPartId", value: partId },
      { key: "SetComponentId", value: componentId  }
    ]

    console.log(params);
    return params;
  });

  constructor(
    private msalBroadcastService: MsalBroadcastService,          
    private applicationSecurity: ApplicationSecurity,
    private securityConfig: SecurityConfig,
    private messenger: Messenger
  ) {
      this.catalogMenuChanged = this.messenger.getSignal(CatalogDataChangedMessage);

      this.messenger.subscribe(this, PrintRoomPropertiesMessage, async (msg) => {
        if (msg.handled) return;
        msg.handled = true;
        await this.printRoomProperties();
      });

      this.messenger.subscribe(this, BreadcrumbChangedMessage, (msg) => {
        if(msg.breadcrumbName !== CommonConstants.CATALOG) return;
        this.currentBreadcrumbs = msg.breadcrumbs;
      });
  }

  public roomHeader = computed(() => {
    const code = this.roomPropertyResource.value().code();
    const subcode = this.roomPropertyResource.value().subCode();
    const name = this.roomPropertyResource.value().name();
    
    const parts = [];
    
    // Add code if it exists and differs from name
    if (code && code !== name) {
      parts.push(code);
    }
    
    // Add subcode if it exists and differs from name
    if (subcode && subcode !== name) {
      if (parts.length > 0) {
        parts[0] += '-' + subcode;
      } else {
        parts.push(subcode);
      }
    }
    
    // Add name with separator if we have code/subcode
    if (parts.length > 0) {
      parts.push(' - ');
    }
    parts.push(name);
    
    return parts.join('');
  });


  private GetRefreshToken() {
    let keys = Object.keys(localStorage);
    let refreshTokenKey = keys.find(key => key.includes('refreshtoken'));

    if (refreshTokenKey) {
      let refreshTokenValue = localStorage.getItem(refreshTokenKey);
      if (!refreshTokenValue) return undefined;

      var refreshTokenObj = JSON.parse(refreshTokenValue);
      return refreshTokenObj["secret"];      
    }
    
    return undefined;
  }

  public ngOnInit(): void {
    this.catalogStateService.initialize(this.route);

    this.messenger.send(new ShowPrintRoomPropertiesMessage());


    // Subscribe to the msal notifications to get the access token
    this._authSubscription = this.msalBroadcastService.msalSubject$
        .pipe(
            filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.SSO_SILENT_SUCCESS),
            takeUntil(this._destroying$)
        )
        .subscribe(async (result: EventMessage) => {
            // Casting payload as AuthenticationResult to access account
            let authenticationResult = result.payload as AuthenticationResult;            
            
            this._accessToken.set(authenticationResult.accessToken);

        });


    this.route.queryParamMap.pipe(
      takeUntil(this._destroying$)
    ).subscribe((params) => {
      const roomId = params.get(CatalogCommonConstants.ROUTE_PARAMS.ROOM_ID);

      this.selectedCategoryId.set(params.get(CatalogCommonConstants.ROUTE_PARAMS.CATEGORY_ID));

      if (!roomId) {
        this.navigateToListPage();
      }

    });

    this.removeSearchBreadcrumbs();
  }


  public navigateToListPage() {
    this.router.navigate(["/catalog"]);
  }

  public open3DViewer(): void {
    this.is3DViewerEnable.set(true);
  }

  public close3DViewer(): void {
    this.is3DViewerEnable.set(false);
  }

  public onCategorySelected(category: CategoryModel) {
      this.selectedCategoryId.set(category.id);
      this.messenger.send(new BreadcrumbItemToggleMessage(category, CatalogConstants.CATALOG));
  }  

  private async printRoomProperties(): Promise<void> {
    await this.catalogStateService.printRoomProperties(this.roomId());
  }


  private removeSearchBreadcrumbs() {
    this.currentBreadcrumbs.forEach(cb => {
      if(cb instanceof SearchModel) {
        this.messenger.send(new BreadcrumbItemToggleMessage(cb, CatalogConstants.CATALOG));
      }
    });
  }

  ngOnDestroy(): void {
    this.messenger.send(new HidePrintRoomPropertiesMessage());
    this.messenger.unsubscribeByOwner(this);
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
